import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Users, Pencil, Trash2, Trees, MessageCircle, Ban } from "lucide-react";
import { BackLink, DetailHeader, Panel, InfoGrid, ActionButton, AddButton, ListCard, Empty, ConfirmDelete } from "@/components/Bits";
import { ClienteFormDialog } from "@/components/ClienteFormDialog";
import { FazendaFormDialog } from "@/components/FazendaFormDialog";
import { OSHistory } from "@/components/OSHistory";
import { DocumentosPanel } from "@/components/DocumentosPanel";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";

export default function ClienteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: c, reload } = useFetch(`/clientes/${id}`);
  const { data: fazendas, reload: reloadFaz } = useFetch("/fazendas", { cliente_id: id });
  const [edit, setEdit] = useState(false);
  const [newFaz, setNewFaz] = useState(false);
  const [del, setDel] = useState(false);
  const [blk, setBlk] = useState(false);
  if (!c) return null;

  const remove = async () => {
    try { await api.delete(`/clientes/${id}`); toast.success("Cliente excluído"); navigate("/clientes"); }
    catch (e) { toast.error(errMsg(e)); setDel(false); }
  };
  const block = async () => {
    try { await api.post(`/clientes/${id}/bloquear`); toast.success("Cliente bloqueado"); setBlk(false); reload(); }
    catch (e) { toast.error(errMsg(e)); setBlk(false); }
  };
  const wpp = (c.whatsapp || "").replace(/\D/g, "");

  return (
    <div data-testid="cliente-detail-page">
      <BackLink to="/clientes" label="Clientes" />
      <DetailHeader eyebrow="Cliente" title={c.nome} subtitle={c.documento} icon={Users} actions={<>
        {wpp && <a href={`https://wa.me/55${wpp}`} target="_blank" rel="noreferrer" data-testid="cliente-whatsapp-link"
          className="h-12 px-4 rounded-xl font-semibold flex items-center gap-2 bg-[#25D366] text-white hover:opacity-90"><MessageCircle className="h-5 w-5" /> WhatsApp</a>}
        <ActionButton icon={Pencil} onClick={() => setEdit(true)} data-testid="cliente-edit-button">Editar</ActionButton>
        {c.bloqueado ? <span className="h-12 px-4 rounded-xl bg-[#B05A40] text-white font-semibold flex items-center gap-2" data-testid="cliente-bloqueado-badge"><Ban className="h-5 w-5" /> Bloqueado</span>
          : <ActionButton icon={Ban} variant="danger" onClick={() => setBlk(true)} data-testid="cliente-block-button">Bloquear</ActionButton>}
        <ActionButton icon={Trash2} variant="danger" onClick={() => setDel(true)} data-testid="cliente-delete-button">Excluir</ActionButton>
      </>} />
      <div className="space-y-5">
        <Panel title="Dados do cliente" testId="cliente-info">
          <InfoGrid items={[["Nome / Razão Social", c.nome], ["CPF / CNPJ", c.documento], ["Inscrição estadual", c.inscricao_estadual], ["Responsável", c.responsavel], ["Telefone", c.telefone],
            ["WhatsApp", c.whatsapp], ["E-mail", c.email], ["Acesso ao app", c.conta_acesso ? `${c.conta_acesso.email}${c.conta_acesso.active ? "" : " (inativo)"}` : "Sem conta vinculada"],
            ["Observações", c.observacoes]]} />
        </Panel>
        <Panel title={`Fazendas (${fazendas?.length ?? 0})`} testId="cliente-fazendas"
          action={<AddButton small onClick={() => setNewFaz(true)} data-testid="cliente-add-fazenda">Fazenda</AddButton>}>
          {fazendas?.length === 0 && <Empty>Nenhuma fazenda cadastrada.</Empty>}
          <div className="space-y-3">
            {(fazendas || []).map((f) => (
              <ListCard key={f.fazenda_id} to={`/fazendas/${f.fazenda_id}`} icon={Trees} testId={`fazenda-card-${f.fazenda_id}`}
                title={f.nome} subtitle={[f.municipio, f.estado].filter(Boolean).join(" / ")} meta={`${f.total_equipamentos} equipamento(s)`} />
            ))}
          </div>
        </Panel>
        <OSHistory filter={{ cliente_id: id }} newQuery={{ cliente_id: id }} />
        <DocumentosPanel filter={{ cliente_id: id }} fixed={{ cliente_id: id }} />
      </div>
      <ClienteFormDialog open={edit} onOpenChange={setEdit} editing={c} onSaved={reload} />
      <FazendaFormDialog open={newFaz} onOpenChange={setNewFaz} clienteId={id} onSaved={reloadFaz} />
      <ConfirmDelete open={blk} onOpenChange={setBlk} title="Bloquear cliente?" description="CPF/CNPJ, e-mail e inscrição estadual serão bloqueados e o cliente nunca mais terá acesso ao aplicativo." onConfirm={block} />
      <ConfirmDelete open={del} onOpenChange={setDel} title="Excluir cliente?" description="Só é possível excluir clientes sem fazendas e sem OS." onConfirm={remove} />
    </div>
  );
}
